import { useSyncExternalStore } from "react";
import { ru, type Messages } from "./ru";
import { kk } from "./kk";

export type Lang = "ru" | "kk";
export const LANGS: Lang[] = ["ru", "kk"];
export type { Messages };

const CATALOGS: Record<Lang, Messages> = { ru, kk };
const STORAGE_KEY = "app-lang";

function initialLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "ru" || saved === "kk") return saved;
  } catch {
    // Storage blocked (private mode): fall through to the browser language.
  }
  return typeof navigator !== "undefined" && /^kk\b/i.test(navigator.language) ? "kk" : "ru";
}

let current: Lang = initialLang();
const listeners = new Set<() => void>();
if (typeof document !== "undefined") document.documentElement.lang = current;

export function getLang(): Lang {
  return current;
}

/**
 * The interface language is the visitor's choice, kept on this device. A studio
 * that offers fewer languages applies one without persisting it (persist: false),
 * so the visitor's own choice survives for other studios.
 */
export function setLang(lang: Lang, persist = true) {
  if (persist) {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // The choice still applies for this visit.
    }
  }
  if (lang === current) return;
  current = lang;
  if (typeof document !== "undefined") document.documentElement.lang = lang;
  for (const l of listeners) l();
}

/** Fit the interface to the languages a studio offers: the visitor's choice if offered, else the studio's first. */
export function applyStudioLanguages(allowed: readonly Lang[]) {
  const preferred = initialLang();
  const next = allowed.includes(preferred) ? preferred : allowed[0]!;
  if (next !== current) setLang(next, false);
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useLang(): Lang {
  return useSyncExternalStore(subscribe, getLang, getLang);
}

/** Copy for the current language; re-renders the caller when the language changes. */
export function useT(): Messages {
  return CATALOGS[useLang()];
}

/** Copy outside React (error mapping, formatting): read at call time. */
export function t(): Messages {
  return CATALOGS[current];
}

/** BCP 47 tag for Intl number formatting, falling back to Russian where Kazakh data is missing. */
export function intlLocale(lang: Lang = current): string {
  const tag = lang === "kk" ? "kk-KZ" : "ru-RU";
  return Intl.NumberFormat.supportedLocalesOf([tag]).length ? tag : "ru-RU";
}
